// client/src/components/admin/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    users: { total: 0, pending: 0 },
    organizations: { total: 0, companies: 0, banks: 0 },
    requests: { total: 0, active: 0 },
    bids: { total: 0, active: 0 },
    liquidity: { total: 0, allocated: 0, available: 0 },
    recent: { requests: [], bids: [] }
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get('/api/admin/stats');
        // Get count of pending users
        const pendingUsersRes = await axios.get('/api/admin/pending-users');
        const pendingUsersCount = pendingUsersRes.data.pendingUsers.length;
        
        // Validate the response data structure before setting state
        if (res.data) {
          // Ensure all expected properties exist with proper defaults
          const validatedStats = {
            users: { 
              total: res.data.users?.total || 0,
              pending: pendingUsersCount || 0
            },
            organizations: { 
              total: res.data.organizations?.total || 0, 
              companies: res.data.organizations?.companies || 0, 
              banks: res.data.organizations?.banks || 0 
            },
            requests: { 
              total: res.data.requests?.total || 0, 
              active: res.data.requests?.active || 0 
            },
            bids: { 
              total: res.data.bids?.total || 0, 
              active: res.data.bids?.active || 0 
            },
            liquidity: { 
              total: res.data.liquidity?.total || 0, 
              allocated: res.data.liquidity?.allocated || 0, 
              available: res.data.liquidity?.available || 0 
            },
            recent: { 
              requests: Array.isArray(res.data.recent?.requests) ? res.data.recent.requests : [],
              bids: Array.isArray(res.data.recent?.bids) ? res.data.recent.bids : []
            }
          };
          setStats(validatedStats);
        }
        setLoading(false);
      } catch (err) {
        console.error("Error fetching stats:", err);
        setError('Failed to fetch system statistics');
        setLoading(false);
      }
    };
    
    fetchStats();
  }, []);
  
  if (loading) {
    return <div className="loading">Loading dashboard data...</div>;
  }
  
  if (error) {
    return <div className="error-message">{error}</div>;
  }
  
  // Helper function to safely format numbers
  const formatNumber = (num) => (num || 0).toLocaleString();
  
  // Helper function to format currency with currency code
  const formatCurrency = (amount, currency = 'EUR') => {
    if (amount === undefined || amount === null) return `${currency} 0`;
    return `${currency} ${(Number(amount) || 0).toLocaleString('en-US')}`;
  };
  
  return (
    <div className="admin-dashboard">
      <h2>System Overview</h2>
      
      <div className="stats-grid">
  <div className="stat-card">
    <h3>Users</h3>
    <div className="stat-value">{formatNumber(stats.users.total)}</div>
    <div className="stat-details">
      {stats.users.pending > 0 && (
        <div className="pending-alert">
          <span>{formatNumber(stats.users.pending)} pending</span>
        </div>
      )}
    </div>
    <div className="stat-actions">
      <Link to="/admin/users">View All Users</Link>
      {stats.users.pending > 0 && (
        <Link to="/admin/pending-users" className="pending-link">
          Review Pending Users
        </Link>
      )}
    </div>
  </div>
  
  <div className="stat-card">
    <h3>Organizations</h3>
    <div className="stat-value">{formatNumber(stats.organizations.total)}</div>
    <div className="stat-details">
      <div>Banks: {formatNumber(stats.organizations.banks)}</div>
      <div>Companies: {formatNumber(stats.organizations.companies)}</div>
    </div>
    <div className="stat-actions">
      <Link to="/admin/organizations">View All Organizations</Link>
    </div>
  </div>
  
  <div className="stat-card">
    <h3>Liquidity Requests</h3>
    <div className="stat-value">{formatNumber(stats.requests.total)}</div>
    <div className="stat-details">
      <div>Active: {formatNumber(stats.requests.active)}</div>
    </div>
    <div className="stat-actions">
      <Link to="/admin/requests">View All Requests</Link>
    </div>
  </div>
  
  <div className="stat-card">
    <h3>Total Liquidity</h3>
    <div className="stat-value">{formatCurrency(stats.liquidity.total, 'EUR')}</div>
    <div className="stat-details">
      <div>Allocated: {formatCurrency(stats.liquidity.allocated, 'EUR')}</div>
      <div>Available: {formatCurrency(stats.liquidity.available, 'EUR')}</div>
    </div>
  </div>
</div>
      
      <div className="recent-activity">
        <div className="recent-section">
          <h3>Recent Requests</h3>
          {stats.recent.requests.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Company</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent.requests.map((request, index) => (
                  <tr key={request._id || `request-${index}`}>
                    <td>#{request.requestId || 'N/A'}</td>
                    <td>{request.company?.name || 'N/A'}</td>
                    <td>{formatCurrency(request.amount, request.currency)}</td>
                    <td>
                      <span className={`status status-${request.status || 'unknown'}`}>
                        {(request.status || 'unknown').replace('_', ' ')}
                      </span>
                    </td>
                    <td>{request.createdAt ? new Date(request.createdAt).toLocaleDateString() : 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No recent requests</p>
          )}
        </div>
        
        <div className="recent-section">
          <h3>Recent Bids</h3>
          {stats.recent.bids.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th>Request</th>
                  <th>Bank</th>
                  <th>Amount</th>
                  <th>Rate</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent.bids.map((bid, index) => (
                  <tr key={bid._id || `bid-${index}`}>
                    <td>#{bid.request?.requestId || 'N/A'}</td>
                    <td>{bid.bank?.name || 'N/A'}</td>
                    <td>{formatCurrency(bid.amount, bid.currency)}</td>
                    <td>{bid.interestRate || 0}%</td>
                    <td>
                      <span className={`status status-${bid.status || 'unknown'}`}>
                        {bid.status || 'unknown'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No recent bids</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;